/* eslint-disable import/no-cycle */
/* eslint-disable react/require-default-props */
import React, { useCallback, ReactNode } from 'react'
import { graphql } from 'gatsby'
import tw, { css } from 'twin.macro'
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import {
  MARKS,
  BLOCKS,
  INLINES,
  Hyperlink,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  Paragraph,
  OrderedList as OrderedListType,
  UnorderedList as UnorderedListType,
  ListItem as ListItemType,
  Table,
  TableCell,
  TableHeaderCell,
  TableRow,
  Node,
} from '@contentful/rich-text-types'
import { RenderRichTextData, ContentfulRichTextGatsbyReference } from 'gatsby-source-contentful/rich-text'

import Link from '@/components/link-component'
import HeadingWithAnchor from '@/components/heading-with-anchor'
import BwTable from '@/components/ui/table/bw-table'
import colors from '@/constants/colors'
import { defaultLocale, localizedPath, useLocale } from '@plugins/bw-i18next'
import { useLocation } from '@reach/router'

import HeaderCard from '@/components/header-card'
// eslint-disable-next-line import/no-self-import
import RichTextComponent from './rich-text'
import Callout, { Props as CalloutProps } from './callout'
import Code, { Props as CodeProps } from './code'
import Cta from './cta'
import HubSpotForm from './hubspot-form'
import Icon from './icon-font'
import Image from './image'
import Quote, { QuoteProps } from './quote'
import Tabs, { Props as TabsProps } from './tabs'
import Video from './video'
import OverflowIndicator from './overflow-indicator'
import CtaLink from './cta-link'

interface Props {
  body: RenderRichTextData<ContentfulRichTextGatsbyReference>
  contentWidth?: string
  backgroundColor?: string
  tableColor?: string
  customListIcon?: Queries.IconFragment
  h1?: (node: Heading1, children: ReactNode) => ReactNode
  h2?: (node: Heading2, children: ReactNode) => ReactNode
  h3?: (node: Heading3, children: ReactNode) => ReactNode
  h4?: (node: Heading4, children: ReactNode) => ReactNode
  h5?: (node: Heading5, children: ReactNode) => ReactNode
  h6?: (node: Heading6, children: ReactNode) => ReactNode
  p?: (node: Paragraph, children: ReactNode) => ReactNode
  blockquote?: (node: Paragraph, children: ReactNode) => ReactNode
  ol?: (node: OrderedListType, children: ReactNode) => ReactNode
  ul?: (node: UnorderedListType, children: ReactNode) => ReactNode
  li?: (node: ListItemType, children: ReactNode) => ReactNode
  table?: (node: Table, children: ReactNode) => ReactNode
  tableCell?: (node: TableCell, children: ReactNode) => ReactNode
  tableHeaderCell?: (node: TableHeaderCell, children: ReactNode) => ReactNode
  tableRow?: (node: TableRow, children: ReactNode) => ReactNode
  a?: (node: Hyperlink, children: ReactNode) => ReactNode
  bold?: (text: ReactNode) => ReactNode
  code?: (text: ReactNode) => ReactNode
  hr?: () => ReactNode
  renderCallout?: (ref: CalloutProps & ContentfulRichTextGatsbyReference) => ReactNode
  renderCode?: (ref: CodeProps & ContentfulRichTextGatsbyReference) => ReactNode
  renderCta?: (ref) => ReactNode
  renderHubSpotForm?: (ref) => ReactNode
  renderIcon?: (ref) => ReactNode
  renderImage?: (ref) => ReactNode
  renderComponentImage?: (ref) => ReactNode
  renderQuote?: (ref: QuoteProps & ContentfulRichTextGatsbyReference) => ReactNode
  renderTabs?: (ref: TabsProps & ContentfulRichTextGatsbyReference) => ReactNode
  renderVideo?: (ref) => ReactNode
  renderCtaLink?: (ref) => ReactNode
  renderHeaderCard?: (ref) => ReactNode
  renderRichText?: (ref) => ReactNode
}

export default function RichText({
  contentWidth = 'narrow',
  tableColor = 'gray',
  backgroundColor = colors.white,
  body,
  customListIcon,
  h1 = (node, children) => <h1>{children}</h1>,
  h2 = (node) => <HeadingWithAnchor type="h2" title={documentToPlainTextString(node)} />,
  h3 = (node) => <HeadingWithAnchor type="h3" title={documentToPlainTextString(node)} />,
  h4 = (node, children) => <h4>{children}</h4>,
  h5 = (node, children) => <h5>{children}</h5>,
  h6 = (node, children) => <h6>{children}</h6>,
  p = (node, children) => <p>{children}</p>,
  blockquote = (node, children) => (
    <blockquote
      tw="font-medium leading-normal text-indigoBlue text-left border-l-8 border-primaryBlue my-8 py-4 pr-0 pl-6"
      css={[
        css`
          * {
            ${tw`text-xl`}
          }
          p:last-child {
            ${tw`m-0`}
          }
        `,
      ]}
    >
      {children}
    </blockquote>
  ),
  ol = (node, children) => <ol tw="mb-4 ml-5 pl-0">{children}</ol>,
  ul = (node, children) => <ul tw="mb-4 ml-5 pl-0">{children}</ul>,
  li = (node, children) => (
    <li
      className={customListIcon ? 'custom-list-icon' : ''}
      css={[
        css`
          ${tw`relative`}
          p {
            ${tw`mb-2`}
          }
          &.custom-list-icon {
            ${tw`list-none`}
          }
        `,
      ]}
    >
      {customListIcon && <Icon {...customListIcon} tw="absolute top-0 -left-7" />}
      {children}
    </li>
  ),
  table = (node, children) => {
    let thead = null
    const trChildren = []
    React.Children.forEach(children, (child: ReactNode & { type: string }) => {
      if (child.type === 'thead') thead = child
      else if (child.type === 'tr') trChildren.push(child)
    })
    return (
      <div className="table-container" css={contentWidth === 'wide' && tw`pt-5 lg:pt-8`}>
        <OverflowIndicator tw="mb-5 lg:mb-10 overflow-x-auto [*>p]:mb-0" disableOverflowY threshold={24}>
          <BwTable tableColor={tableColor} contentWidth={contentWidth}>
            {thead}
            {trChildren.length > 0 && <tbody>{trChildren}</tbody>}
          </BwTable>
        </OverflowIndicator>
      </div>
    )
  },
  tableCell = (node) => (
    <td tw="relative">
      <RichTextComponent
        body={{
          raw: JSON.stringify({
            nodeType: BLOCKS.DOCUMENT,
            data: {},
            content: node.content,
          }),
          references: body.references,
        }}
      />
    </td>
  ),
  tableHeaderCell = (node) => (
    <th>
      <RichTextComponent
        body={{
          raw: JSON.stringify({
            nodeType: BLOCKS.DOCUMENT,
            data: {},
            content: node.content,
          }),
          references: body.references,
        }}
      />
    </th>
  ),
  tableRow = (node, children) => {
    const childType = node.content[0].nodeType
    if (BLOCKS.TABLE_HEADER_CELL === childType)
      return (
        <thead>
          <tr>{children}</tr>
        </thead>
      )
    if (BLOCKS.TABLE_CELL === childType) return <tr>{children}</tr>
  },
  a = (node, children) => (
    <Link
      to={node.data.uri}
      tw="underline"
      css={[
        backgroundColor === colors.white && tw`text-primaryBlue`,
        backgroundColor === colors.lightGray && tw`text-primaryBlue`,
        backgroundColor === colors.primaryBlue && tw`text-white`,
        backgroundColor === colors.indigoBlue && tw`text-tealBlue`,
        tw`dark:(text-tealBlue hover:text-white)`,
      ]}
    >
      {children}
    </Link>
  ),
  bold = (text) => <strong>{text}</strong>,
  code = (text) => <code style={{ color: '#e83e8c' }}>{text}</code>,
  hr = () => <hr />,
  renderCallout = (ref) => (
    <Callout {...ref}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <RichTextComponent body={ref.body} />
    </Callout>
  ),
  renderCode = (ref) => <Code body={ref.body} />,
  renderCta = (ref) => <Cta {...ref} />,
  renderHubSpotForm = (ref) => <HubSpotForm {...ref} />,
  renderIcon = (ref) => <Icon className="rich-icon" {...ref} />,
  renderImage = (ref) => <Image image={ref} showCaption />,
  renderComponentImage = (ref) => <Image {...ref} />,
  renderTabs = (ref) => <Tabs groupId={ref.contentful_id} body={ref.body} />,
  renderQuote = (ref) => <Quote attribution={ref.attribution} body={ref.body} citation={ref.citation} />,
  renderVideo = (ref) => (
    <div tw="mb-8">
      <Video {...ref} />
    </div>
  ),
  renderCtaLink = (ref) => <CtaLink {...ref} />,
  renderHeaderCard = (ref) => <HeaderCard {...ref} />,
  renderRichText = (ref) => <RichText {...ref} />,
}: Props) {
  if (typeof body !== 'object' || !body) return null

  const getRef = (node: Node) =>
    body.references?.find((ref) => ref.contentful_id === node?.data?.target?.sys?.id) || node?.data?.target
  const optionsObj: Options = {
    renderNode: {
      [BLOCKS.HEADING_1]: useCallback(h1, []),
      [BLOCKS.HEADING_2]: useCallback(h2, []),
      [BLOCKS.HEADING_3]: useCallback(h3, []),
      [BLOCKS.HEADING_4]: useCallback(h4, []),
      [BLOCKS.HEADING_5]: useCallback(h5, []),
      [BLOCKS.HEADING_6]: useCallback(h6, []),
      [BLOCKS.PARAGRAPH]: useCallback(p, []),
      [BLOCKS.QUOTE]: useCallback(blockquote, []),
      [BLOCKS.OL_LIST]: useCallback(ol, []),
      [BLOCKS.UL_LIST]: useCallback(ul, []),
      [BLOCKS.LIST_ITEM]: useCallback(li, []),
      [BLOCKS.TABLE]: useCallback(table, []),
      [BLOCKS.TABLE_CELL]: useCallback(tableCell, []),
      [BLOCKS.TABLE_HEADER_CELL]: useCallback(tableHeaderCell, []),
      [BLOCKS.TABLE_ROW]: useCallback(tableRow, []),
      [BLOCKS.HR]: useCallback(hr, []),
      [BLOCKS.EMBEDDED_ENTRY]: useCallback((node) => {
        const ref = getRef(node)
        if (!ref) return
        if (ref.__typename === 'ContentfulComponentCallout')
          return renderCallout(ref as CalloutProps & ContentfulRichTextGatsbyReference)
        if (ref.__typename === 'ContentfulComponentCode')
          return renderCode(ref as CodeProps & ContentfulRichTextGatsbyReference)
        if (ref.__typename === 'ContentfulComponentCta') return renderCta(ref)
        if (ref.__typename === 'ContentfulComponentHubSpotForm') return renderHubSpotForm(ref)
        if (ref.__typename === 'ContentfulComponentImage') return renderComponentImage(ref)
        if (ref.__typename === 'ContentfulComponentQuote')
          return renderQuote(ref as QuoteProps & ContentfulRichTextGatsbyReference)
        if (ref.__typename === 'ContentfulComponentTabs')
          return renderTabs(ref as TabsProps & ContentfulRichTextGatsbyReference)
        if (ref.__typename === 'ContentfulComponentVideo') return renderVideo(ref)
        if (ref.__typename === 'ContentfulComponentIcon') return renderIcon(ref)
        if (ref.__typename === 'ContentfulComponentRichText') return renderRichText(ref)
      }, []),
      [BLOCKS.EMBEDDED_ASSET]: useCallback((node) => {
        const ref = getRef(node)
        if (!ref) return null
        return renderImage(ref)
      }, []),
      [INLINES.ASSET_HYPERLINK]: useCallback((node) => {
        const ref = getRef(node) as ContentfulRichTextGatsbyReference & Queries.AssetFragment
        if (!ref) return
        return (
          <Link
            title={`${ref.title} | ${ref.description}`}
            to={ref.file?.url || ref.publicUrl}
            tw="text-primaryBlue hover:text-indigoBlue transition-colors"
          >
            <RichTextComponent
              body={{
                raw: JSON.stringify({
                  nodeType: BLOCKS.DOCUMENT,
                  data: {},
                  content: node.content,
                }),
                references: body.references,
              }}
            />
          </Link>
        )
      }, []),
      [INLINES.EMBEDDED_ENTRY]: useCallback((node) => {
        const ref = getRef(node) as ContentfulRichTextGatsbyReference
        if (!ref) return
        if (ref.__typename === 'ContentfulComponentIcon') return renderIcon(ref)
        if (ref.__typename === 'ContentfulComponentImage') return renderComponentImage(ref)
        if (ref.__typename === 'ContentfulComponentCtaLink') return renderCtaLink(ref)
        if (ref.__typename === 'ContentfulHeaderCard') return renderHeaderCard(ref)
      }, []),
      [INLINES.HYPERLINK]: useCallback(a, []),
      [INLINES.ENTRY_HYPERLINK]: useCallback((node) => {
        const ref = getRef(node)

        let path
        if (ref?.__typename === 'ContentfulSeo') path = (ref as unknown as Queries.ContentfulSeo)?.path
        else if (ref?.__typename === 'ContentfulHelpArticle')
          path = `/help/${(ref as unknown as Queries.ContentfulHelpArticle)?.slug}/${
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (node.content?.[0] as any)?.hash || ''
          }`
        else if (ref?.__typename === 'ContentfulComponentLink') {
          path = (ref as unknown as Queries.ContentfulComponentLink)?.url
        }

        const { currentLocale } = useLocale()
        path = localizedPath(path, currentLocale)

        // localize slugs are not correct atm
        if (
          currentLocale !== defaultLocale &&
          path.includes('#') &&
          !path.includes('#osano') &&
          path.startsWith('/') &&
          !path.startsWith('//')
        ) {
          const { pathname: currentPathname } = useLocation()
          const { pathname: targetPathname } = new URL(`https://bitwarden.com${path}`)

          if (localizedPath(currentPathname, defaultLocale) === localizedPath(targetPathname, defaultLocale)) path = ''
          else path = targetPathname
        }

        if (!path)
          return (
            <RichTextComponent
              body={{
                raw: JSON.stringify({
                  nodeType: BLOCKS.DOCUMENT,
                  data: {},
                  content: node.content,
                }),
                references: body.references,
              }}
            />
          )
        return (
          <Link
            to={path}
            tw="underline"
            css={[
              backgroundColor === colors.white && tw`text-primaryBlue`,
              backgroundColor === colors.lightGray && tw`text-primaryBlue`,
              backgroundColor === colors.primaryBlue && tw`text-white`,
              backgroundColor === colors.indigoBlue && tw`text-tealBlue`,
              tw`dark:(text-tealBlue hover:text-white)`,
            ]}
          >
            <RichTextComponent
              body={{
                raw: JSON.stringify({
                  nodeType: BLOCKS.DOCUMENT,
                  data: {},
                  content: node.content,
                }),
                references: body.references,
              }}
            />
          </Link>
        )
      }, []),
    },
    renderMark: {
      [MARKS.BOLD]: useCallback(bold, []),
      [MARKS.CODE]: useCallback(code, []),
    },
    renderText: (text) =>
      text
        .split('\n')
        .reduce((children, textSegment, index) => [...children, index > 0 && <br key={index} />, textSegment], []),
  }

  return documentToReactComponents(JSON.parse(body.raw), optionsObj) as JSX.Element
}

export const fragment = graphql`
  fragment Asset on ContentfulAsset {
    id
    contentful_id
    title
    description
    file {
      contentType
      url
    }
    publicUrl
    svg {
      content
    }
    height
    width
  }
  fragment RichText on ContentfulComponentRichText {
    __typename
    contentful_id
    body {
      raw
      references {
        ...Asset
        ...Icon
        ...Link
      }
    }
    customListIcon {
      ...Icon
    }
  }
`
